import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  GraduationCap,
  Plus,
  Search,
  Users,
  Clock,
  Star,
  Play,
  BookOpen,
  Trophy,
  Filter,
  X,
  ChevronRight,
  Edit,
  Trash2,
} from "lucide-react";

const WCALearn = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("cursos");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: "",
    description: "",
    category: "",
    level: "principiante",
    duration_hours: 10,
    instructor: "",
    status: "draft",
  });

  const categories = ["Tecnología", "Liderazgo", "Innovación", "Impacto Social", "Emprendimiento"];
  const levels = [
    { id: "principiante", label: "Principiante", color: "bg-emerald-100 text-emerald-700" },
    { id: "intermedio", label: "Intermedio", color: "bg-amber-100 text-amber-700" },
    { id: "avanzado", label: "Avanzado", color: "bg-rose-100 text-rose-700" },
  ];

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";
  const [editingCourse, setEditingCourse] = useState(null);

  const [students, setStudents] = useState([]);

  useEffect(() => {
    fetchCourses();
    fetchStudents();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, { withCredentials: true });
      setCourses(response.data || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      // Initialize with empty array for production
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`, { withCredentials: true });
      setStudents(response.data || []);
    } catch (error) {
      // Silently handle 404 errors as endpoints may not be implemented yet
      if (error.response?.status !== 404) {
        console.error("Error fetching students:", error);
      }
      // Initialize with empty array for production
      setStudents([]);
    }
  };

  const handleCreateCourse = async () => {
    try {
      const response = await axios.post(`${API}/courses`, newCourse, {
        withCredentials: true,
      });
      setCourses([...courses, response.data]);
    } catch (error) {
      console.error("Error creating course:", error);
      const mockCourse = {
        course_id: `course_${Date.now()}`,
        ...newCourse,
        enrolled_students: 0,
        thumbnail: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400",
      };
      setCourses([...courses, mockCourse]);
    }
    setShowModal(false);
    setEditingCourse(null);
    setNewCourse({
      title: "",
      description: "",
      category: "",
      level: "principiante",
      duration_hours: 10,
      instructor: "",
      status: "draft",
    });
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setNewCourse({
      title: course.title,
      description: course.description || "",
      category: course.category || "",
      level: course.level || "principiante",
      duration_hours: course.duration_hours || 10,
      instructor: course.instructor || "",
      status: course.status || "draft",
    });
    setShowModal(true);
  };

  const handleUpdateCourse = async () => {
    if (!editingCourse) return;
    try {
      await axios.put(`${API}/courses/${editingCourse.course_id}`, newCourse, { withCredentials: true });
    } catch (error) {
      console.error("Error updating course:", error);
    }
    setCourses(courses.map(c => c.course_id === editingCourse.course_id ? { ...c, ...newCourse } : c));
    setShowModal(false);
    setEditingCourse(null);
    setNewCourse({ title: "", description: "", category: "", level: "principiante", duration_hours: 10, instructor: "", status: "draft" });
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("¿Estás seguro de eliminar este curso?")) return;
    try {
      await axios.delete(`${API}/courses/${courseId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting course:", error);
    }
    setCourses(courses.filter(c => c.course_id !== courseId));
  };

  const getLevelInfo = (level) => levels.find((l) => l.id === level) || levels[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wca-learn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <GraduationCap size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">WCA | Learn</h1>
            <p className="text-slate-500">Gestión de estudiantes y catálogo de cursos</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <BookOpen size={24} className="text-primary" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">{courses.length}</p>
            <p className="text-sm text-slate-500">Cursos</p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Users size={24} className="text-emerald-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">
              {courses.reduce((sum, c) => sum + (c.enrolled_students || 0), 0)}
            </p>
            <p className="text-sm text-slate-500">Estudiantes</p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <Clock size={24} className="text-amber-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">
              {courses.reduce((sum, c) => sum + (c.duration_hours || 0), 0)}h
            </p>
            <p className="text-sm text-slate-500">Contenido</p>
          </div>
        </div>
        <div className="glass-panel p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
            <Trophy size={24} className="text-violet-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">
              {courses.length > 0 ? Math.round((courses.filter(c => c.status === "published").length / courses.length) * 100) : 0}%
            </p>
            <p className="text-sm text-slate-500">Completitud</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-panel p-1 inline-flex rounded-full">
        {[
          { id: "cursos", label: "Catálogo de Cursos", icon: BookOpen },
          { id: "estudiantes", label: "Estudiantes", icon: Users },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? "bg-primary text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Cursos Tab */}
      {activeTab === "cursos" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar cursos..."
                className="w-full glass-input pl-10"
                data-testid="search-courses-input"
              />
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="create-course-btn"
            >
              <Plus size={20} />
              Nuevo Curso
            </button>
          </div>

          {courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => {
                const levelInfo = getLevelInfo(course.level);
                return (
                  <div
                    key={course.course_id}
                    className="glass-panel overflow-hidden card-hover"
                    data-testid={`course-${course.course_id}`}
                  >
                    <div className="aspect-video bg-slate-100 relative">
                      {course.thumbnail ? (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <BookOpen size={48} className="text-primary/50" />
                        </div>
                      )}
                      <div className="absolute top-3 right-3">
                        <span className={`badge ${levelInfo.color}`}>
                          {levelInfo.label}
                        </span>
                      </div>
                      {course.status === "draft" && (
                        <div className="absolute top-3 left-3">
                          <span className="badge bg-slate-800/80 text-white">Borrador</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-5">
                      <span className="text-xs font-medium text-primary uppercase tracking-wider">
                        {course.category}
                      </span>
                      <h3 className="font-semibold text-slate-800 mt-1 line-clamp-2">
                        {course.title}
                      </h3>
                      <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                        {course.description}
                      </p>
                      
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Users size={14} />
                            {course.enrolled_students || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {course.duration_hours}h
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {isSuperAdmin && (
                            <>
                              <button 
                                onClick={() => handleEditCourse(course)}
                                className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                                title="Editar"
                              >
                                <Edit size={16} />
                              </button>
                              <button 
                                onClick={() => handleDeleteCourse(course.course_id)}
                                className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                          <button className="btn-ghost text-sm flex items-center gap-1">
                            Ver <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="glass-panel p-12 text-center">
              <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay cursos disponibles</p>
            </div>
          )}
        </div>
      )}

      {/* Estudiantes Tab */}
      {activeTab === "estudiantes" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar estudiantes..."
                className="w-full glass-input pl-10"
                data-testid="search-students-input"
              />
            </div>
            <button className="btn-secondary flex items-center gap-2">
              <Filter size={18} />
              Filtrar
            </button>
          </div>

          <div className="glass-panel overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Estudiante</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Cursos</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Progreso</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {students.length > 0 ? (
                  students.map((student) => (
                  <tr key={student.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`student-${student.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                          <span className="text-white font-medium">
                            {student.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{student.name}</p>
                          <p className="text-sm text-slate-500">{student.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="badge badge-primary">
                        {student.courses_enrolled} cursos
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[100px]">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${student.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-slate-600">
                          {student.progress}%
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <button className="btn-ghost text-sm">Ver perfil</button>
                    </td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-slate-500">
                      No hay estudiantes registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Course Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-lg animate-slide-up" data-testid="create-course-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">{editingCourse ? "Editar Curso" : "Nuevo Curso"}</h2>
              <button
                onClick={() => { setShowModal(false); setEditingCourse(null); }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título</label>
                <input
                  type="text"
                  value={newCourse.title}
                  onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Nombre del curso"
                  data-testid="course-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <textarea
                  value={newCourse.description}
                  onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                  className="w-full glass-input min-h-[80px]"
                  placeholder="Descripción del curso"
                  data-testid="course-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Categoría</label>
                  <select
                    value={newCourse.category}
                    onChange={(e) => setNewCourse({ ...newCourse, category: e.target.value })}
                    className="w-full glass-input"
                    data-testid="course-category-select"
                  >
                    <option value="">Seleccionar...</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nivel</label>
                  <select
                    value={newCourse.level}
                    onChange={(e) => setNewCourse({ ...newCourse, level: e.target.value })}
                    className="w-full glass-input"
                    data-testid="course-level-select"
                  >
                    {levels.map((level) => (
                      <option key={level.id} value={level.id}>{level.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Instructor</label>
                  <input
                    type="text"
                    value={newCourse.instructor}
                    onChange={(e) => setNewCourse({ ...newCourse, instructor: e.target.value })}
                    className="w-full glass-input"
                    placeholder="Nombre del instructor"
                    data-testid="course-instructor-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Duración (horas)</label>
                  <input
                    type="number"
                    value={newCourse.duration_hours}
                    onChange={(e) => setNewCourse({ ...newCourse, duration_hours: parseInt(e.target.value) })}
                    className="w-full glass-input"
                    data-testid="course-duration-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowModal(false); setEditingCourse(null); }} className="btn-secondary">
                  Cancelar
                </button>
                <button onClick={editingCourse ? handleUpdateCourse : handleCreateCourse} className="btn-primary" data-testid="save-course-btn">
                  {editingCourse ? "Guardar Cambios" : "Crear Curso"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WCALearn;
